import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useT } from '../i18n/useT';
import { trackEvent } from '../hooks/useTracking';
import { readUrlParams, pushUrlParams, toBackendParams, applyLocationMode } from '../hooks/useSearchUrlSync';
const FIELD_ICONS = {
    keyword: 'briefcase',
    location: 'pin',
    city: 'pin',
    category: 'chevron',
    type: 'chevron',
    experience: 'chevron',
    experienceother: 'chevron',
    client: 'chevron',
};
function FieldIcon({ kind }) {
    if (kind === 'briefcase') {
        return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("rect", { x: "2", y: "7", width: "20", height: "14", rx: "2", ry: "2" }), _jsx("path", { d: "M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" })] }));
    }
    if (kind === 'pin') {
        return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
    }
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("polyline", { points: "6 9 12 15 18 9" }) }));
}
export function SearchBar({ categories, locations, jobWorkTypes, experienceLevels, experienceOtherLevels, clients, filtersOrder, uiConfig, onSearch, variant = 'card' }) {
    const { t } = useT();
    const initial = readUrlParams();
    const [keyword, setKeyword] = useState(initial.keyword);
    // [B-HLPILLNOFILTER] Ha csak kiemelt fő-kategória szűr (?maincategory=X, category nincs), a
    // legördülő X-et mutatja: így a "Minden kategória" választása VALÓDI értékváltás (natív change
    // esemény), ami törli a kiemelt szűrőt - különben a legördülő már a 0-n állna, és ugyanazt
    // újra kiválasztva nem történne semmi. Al-kategóriánál (category=sub) a sub marad.
    const [category, setCategory] = useState(initial.category > 0 ? initial.category : (initial.mainCategory > 0 ? initial.mainCategory : 0));
    const [location, setLocation] = useState(initial.location);
    const [locationText, setLocationText] = useState(initial.locationText);
    const [city, setCity] = useState(initial.city);
    const [jobWorkType, setJobWorkType] = useState(initial.jobWorkType);
    const [experience, setExperience] = useState(initial.experience);
    const [experienceOther, setExperienceOther] = useState(initial.experienceOther);
    const [client, setClient] = useState(initial.client);
    // T-12: extended location keresés — ha be van kapcsolva, a location mező free-text
    const isLocExtended = !!uiConfig?.is_filters_location_extended;
    // A filtersOrder már CSAK az engedélyezett mezőket tartalmazza, a beállított sorrendben.
    // A kikapcsolt mezők a backend OA2 filterSections normalizálásánál szűrve lettek.
    const showField = (name) => filtersOrder.includes(name);
    // Belső helper a keresés tényleges kiváltásához. Az `overrides` paraméter azért kell,
    // mert a select `onChange`-ben hívott `setState` aszinkron — friss értékkel kell hívni.
    const triggerSearch = (overrides = {}, source = 'change', changedField) => {
        const p = {
            keyword: overrides.keyword !== undefined ? overrides.keyword : keyword,
            category: overrides.category !== undefined ? overrides.category : category,
            location: overrides.location !== undefined ? overrides.location : location,
            locationText: overrides.locationText !== undefined ? overrides.locationText : locationText,
            city: overrides.city !== undefined ? overrides.city : city,
            jobWorkType: overrides.jobWorkType !== undefined ? overrides.jobWorkType : jobWorkType,
            experience: overrides.experience !== undefined ? overrides.experience : experience,
            experienceOther: overrides.experienceOther !== undefined ? overrides.experienceOther : experienceOther,
            client: overrides.client !== undefined ? overrides.client : client,
        };
        // [B-CAREERREGIO] egyetlen lokáció-mód szabály (applyLocationMode) - ugyanaz, mint az
        // IndexView URL-indításánál, így az első betöltés és a beküldés azonos feltételt ad.
        const normalized = applyLocationMode({ ...p, mainCategory: 0 }, isLocExtended);
        pushUrlParams({
            keyword: normalized.keyword,
            category: normalized.category,
            location: normalized.location,
            locationText: normalized.locationText,
            city: normalized.city,
            jobWorkType: normalized.jobWorkType,
            experience: normalized.experience,
            experienceOther: normalized.experienceOther,
            client: normalized.client,
        });
        trackEvent('Search', { search_string: p.keyword.trim() || '', content_category: 'Job search' }, 'search', 'careerSearch');
        const backendParams = toBackendParams(normalized, { offset: 0, limit: 20 });
        onSearch(backendParams, source, changedField);
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        // Keresés gomb / Enter: a szülő a találati blokkhoz görget (a select-változás nem).
        triggerSearch({}, 'submit');
    };
    // Render mezők a filtersOrder szerinti sorrendben (T-10 kérés: kereső filterek sorrendje)
    const renderField = (name) => {
        if (!showField(name))
            return null;
        if (name === 'keyword') {
            return (_jsx("input", { type: "text", className: "career-search-input border rounded px-3 py-2", "data-translate-placeholder": "search_placeholder", placeholder: t('search_placeholder', 'Keresés...'), value: keyword, onChange: e => setKeyword(e.target.value) }, "keyword"));
        }
        if (name === 'category' && categories.length > 0) {
            return (_jsxs("select", { className: "career-search-select border rounded px-3 py-2", value: category, onChange: e => {
                    const v = Number(e.target.value);
                    setCategory(v);
                    triggerSearch({ category: v }, 'change', 'category');
                }, children: [_jsx("option", { value: "0", "data-translate": "all_categories", children: t('all_categories', 'Minden kategória') }), categories.map(c => _jsxs("option", { value: c.id, children: [c.name, " (", c.count, ")"] }, c.id))] }, "category"));
        }
        // T-12: extended módban a lokáció mező free-text input (a beírt szöveg → backend city)
        const renderLocationExtInput = (key) => (_jsx("input", { type: "text", className: "career-search-input border rounded px-3 py-2", placeholder: t('all_locations', 'Minden lokáció'), value: locationText, onChange: e => setLocationText(e.target.value) }, key));
        if (name === 'location') {
            if (isLocExtended) {
                return renderLocationExtInput('location-ext');
            }
            if (locations.length === 0)
                return null;
            return (_jsxs("select", { className: "career-search-select border rounded px-3 py-2", value: location, onChange: e => {
                    const v = Number(e.target.value);
                    setLocation(v);
                    triggerSearch({ location: v });
                }, children: [_jsx("option", { value: "0", "data-translate": "all_locations", children: t('all_locations', 'Minden lokáció') }), locations.map(l => _jsxs("option", { value: l.id, children: [l.name, " (", l.count, ")"] }, l.id))] }, "location"));
        }
        if (name === 'city') {
            // [B-CAREERREGIO] konfigurálható szabad-szöveges Város mező (backend `city` → job_data.city
            // LIKE) a régió-select MELLÉ (legacy paritás: Lokáció input + Vármegye select). Extended
            // lokáció-módban a 'location' mező maga a szabad-szöveges város, ezért a külön Város mező
            // nem renderelődik - KIVÉVE, ha a 'location' ki van kapcsolva: akkor a Város mező veszi át
            // a szabad-szöveges lokáció szerepét (locationText), hogy legyen látható városmező.
            if (isLocExtended) {
                return showField('location') ? null : renderLocationExtInput('city-ext');
            }
            return (_jsx("input", { type: "text", className: "career-search-input border rounded px-3 py-2", "data-translate-placeholder": "city_placeholder", placeholder: t('city_placeholder', 'Város'), value: city, onChange: e => setCity(e.target.value) }, "city"));
        }
        if (name === 'type' && jobWorkTypes.length > 0) {
            return (_jsxs("select", { className: "career-search-select border rounded px-3 py-2", value: jobWorkType, onChange: e => {
                    const v = Number(e.target.value);
                    setJobWorkType(v);
                    triggerSearch({ jobWorkType: v });
                }, children: [_jsx("option", { value: "0", "data-translate": "all_work_types", children: t('all_work_types', 'Munkavégzés') }), jobWorkTypes.map(jt => _jsx("option", { value: jt.id, children: jt.name }, jt.id))] }, "type"));
        }
        if (name === 'experience' && experienceLevels.length > 0) {
            return (_jsxs("select", { className: "career-search-select border rounded px-3 py-2", value: experience, onChange: e => {
                    const v = Number(e.target.value);
                    setExperience(v);
                    triggerSearch({ experience: v });
                }, children: [_jsx("option", { value: "0", "data-translate": "all_experience", children: t('all_experience', 'Tapasztalat') }), experienceLevels.map(ex => _jsx("option", { value: ex.id, children: ex.name }, ex.id))] }, "experience"));
        }
        if (name === 'experienceother' && experienceOtherLevels.length > 0) {
            return (_jsxs("select", { className: "career-search-select border rounded px-3 py-2", value: experienceOther, onChange: e => {
                    const v = Number(e.target.value);
                    setExperienceOther(v);
                    triggerSearch({ experienceOther: v });
                }, children: [_jsx("option", { value: "0", "data-translate": "all_experience_other", children: t('all_experience_other', 'Második tapasztalat') }), experienceOtherLevels.map(ex => _jsx("option", { value: ex.id, children: ex.name }, ex.id))] }, "experienceother"));
        }
        if (name === 'client' && clients.length > 0) {
            return (_jsxs("select", { className: "career-search-select border rounded px-3 py-2", value: client, onChange: e => {
                    const v = e.target.value;
                    setClient(v);
                    triggerSearch({ client: v });
                }, children: [_jsx("option", { value: "", "data-translate": "all_clients", children: t('all_clients', 'Cég') }), clients.map((c, i) => _jsx("option", { value: c, children: c }, i))] }, "client"));
        }
        return null;
    };
    // Egységes mező-wrapper mindkét variánsban (.career-search-field) — az ikonok
    // csak az inline (régi karrieroldal) elrendezésben renderelődnek, így a klasszikus
    // nézet változatlan marad. Az ikon a Branding-ből stílusozható/elrejthető
    // (element_key: search_field_icon → .career-search-field-icon).
    const withIcons = variant === 'inline';
    const wrapField = (name) => {
        const el = renderField(name);
        if (!el)
            return null;
        return (_jsxs("div", { className: `career-search-field career-search-field--${name}${withIcons ? ' career-search-field--with-icon' : ''}`, children: [withIcons && (_jsx("span", { className: "career-search-field-icon", children: _jsx(FieldIcon, { kind: FIELD_ICONS[name] || 'chevron' }) })), el] }, `field-${name}`));
    };
    if (variant === 'inline') {
        return (_jsx("form", { className: "career-search-form career-search-form--inline bg-white shadow-md rounded-lg p-4", onSubmit: handleSubmit, children: _jsxs("div", { className: "career-search-form-inline-fields flex flex-wrap gap-3 items-stretch", children: [filtersOrder.map(name => wrapField(name)), _jsx("button", { type: "submit", className: "career-search-submit bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700", "data-translate": "search_submit", children: t('search_submit', 'Keresés') })] }) }));
    }
    return (_jsxs("form", { className: "career-search-form bg-white shadow-md rounded-lg p-6 mb-8", onSubmit: handleSubmit, children: [_jsx("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: filtersOrder.map(name => wrapField(name)) }), _jsx("button", { type: "submit", className: "career-search-submit mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700", "data-translate": "search_submit", children: t('search_submit', 'Keresés') })] }));
}
